<template>
  <div class="house-switcher">
    <component
      :is="switchable ? 'button' : 'div'"
      :type="switchable ? 'button' : null"
      class="house-switcher__trigger"
      :class="{ 'house-switcher__trigger--static': !switchable }"
      :aria-haspopup="switchable ? 'true' : null"
      @click="onTrigger"
    >
      <i class="pi pi-building house-switcher__icon" aria-hidden="true" />
      <span class="house-switcher__meta">
        <span class="house-switcher__label">Auction House</span>
        <span class="house-switcher__name">{{ current ? current.name : 'Auction House' }}</span>
      </span>
      <i
        v-if="switchable"
        :class="['pi', open ? 'pi-angle-up' : 'pi-angle-down', 'house-switcher__caret']"
        aria-hidden="true"
      />
    </component>

    <Popover v-if="switchable" ref="popoverRef" append-to="body" @hide="open = false" @show="onShow">
      <div ref="listRef" class="house-list">
        <button
          v-for="each in houses"
          :key="each.code"
          type="button"
          class="house-list__row"
          :class="{ 'house-list__row--active': each.code === house }"
          @click="onSelect(each)"
        >
          <i v-if="each.code === house" class="pi pi-check house-list__check" aria-hidden="true" />
          <span v-else class="house-list__gap" />
          {{ each.name }}
        </button>
      </div>
    </Popover>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import Popover from 'primevue/popover'

// The auction-house control in the top bar, for a context that stands inside
// one house rather than administering every house. One house renders as a
// static label; two or more make it a switcher. The host owns which house is
// selected and where the roster came from; this only reports the pick.
//
// The shell had this before, as vibe-portal's AuctionHouse.vue, and dropped it
// because the administration context is platform-level. Auction Operations is
// not, so it is back — the same shape as the context selector beside it.
defineOptions({ name: 'HouseSwitcher' })

const props = defineProps({
  // `[{ code, name }]` — the houses the person may stand in.
  houses: { type: Array, default: () => [] },
  // The `code` of the selected house.
  house: { type: String, default: '' },
})
const emit = defineEmits(['select-house'])

const popoverRef = ref(null)
const listRef = ref(null)
const open = ref(false)

const current = computed(() => props.houses.find((each) => each.code === props.house) ?? props.houses[0] ?? null)
const switchable = computed(() => props.houses.length > 1)

function onTrigger(event) {
  if (!switchable.value) return
  popoverRef.value?.toggle(event)
}

// A long roster scrolls inside the panel, so bring the selected house into view.
async function onShow() {
  open.value = true
  await nextTick()
  listRef.value?.querySelector('.house-list__row--active')?.scrollIntoView({ block: 'nearest' })
}

function onSelect(each) {
  popoverRef.value?.hide()
  if (each.code !== props.house) emit('select-house', each.code)
}
</script>

<style scoped>
.house-switcher {
  position: relative;
}

.house-switcher__trigger {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  border: 1px solid var(--p-content-border-color);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  color: var(--p-text-color);
}

.house-switcher__trigger--static {
  cursor: default;
  border-color: transparent;
}

.house-switcher__icon {
  font-size: 15px;
  color: var(--p-surface-400);
  flex-shrink: 0;
}

.house-switcher__meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.house-switcher__label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--p-surface-400);
  line-height: 1;
}

.house-switcher__name {
  font-size: 13px;
  font-weight: 700;
  color: var(--p-text-color);
  line-height: 1.2;
  white-space: nowrap;
}

.house-switcher__caret {
  font-size: 11px;
  color: var(--p-surface-400);
}

.house-list {
  min-width: 220px;
  max-height: min(60vh, 420px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.house-list__row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--p-text-color);
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
}

.house-list__row:hover {
  background: var(--p-overlay-popover-background);
}

.house-list__row--active {
  background: var(--p-highlight-background);
  color: var(--p-primary-600);
}

.house-list__check {
  font-size: 11px;
  flex-shrink: 0;
}

.house-list__gap {
  width: 11px;
  flex-shrink: 0;
}
</style>
